import {computed, Directive, effect, ElementRef, inject, input, output, signal} from '@angular/core';
import {TooltipService} from '../../tooltip';
import {ExtendedTippyInstance} from '@ngneat/helipopper';
import {Content} from '@ngneat/overview';
import {Props} from 'tippy.js';

@Directive({
  selector: '[msTooltip]',
  exportAs: 'msTooltip',
  standalone: true,
  host: {
    '(click)': 'onHostClick($event.target)',
  },
})
export class MsTooltipDirective {
  readonly tooltipValue = input<string | Content>('', {alias: 'msTooltip'});
  readonly options = input<Partial<Props>>({});
  readonly display = input<boolean>(true);

  readonly events = output<any>();

  private readonly optionsOverride = signal<Partial<Props>>({});

  readonly resolvedOptions = computed(() => ({
    ...this.options(),
    ...this.optionsOverride(),
  }));

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly tooltipService = inject(TooltipService);

  private tooltip?: ExtendedTippyInstance<string>;

  constructor() {
    this.handleTooltipChanges();
  }

  onHostClick(eventTarget: EventTarget | null): void {
    const target = eventTarget as HTMLElement;
    target.blur();
    this.tooltip?.hide();
  }

  onShowBefore(): boolean {
    return this.display();
  }

  setOptionsOverride(options: Partial<Props>): void {
    this.optionsOverride.set(options);
  }

  private handleTooltipChanges(): void {
    effect(() => {
      const tooltipValue = this.tooltipValue();

      if (tooltipValue) {
        this.initTooltip(tooltipValue);
      } else {
        this.destroyTooltip();
      }
    });
  }

  private initTooltip(tooltipValue: string | Content): void {
    this.destroyTooltip();

    this.tooltip = this.tooltipService.create(this.elementRef.nativeElement, tooltipValue, {
      ...this.resolvedOptions(),
      onShowBefore: () => this.onShowBefore(),
    });
  }

  private destroyTooltip(): void {
    this.tooltip?.destroy();
    this.tooltip = undefined;
  }
}
